import { LoadingOfferAuthSpinnerProps, LoadingSpinnerProps } from '@/types/props/components';
import translationFallback from '@/utilites/translations/translationFallback';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import { Col, Container, Modal, Row } from 'react-bootstrap';
import { FaWineBottle } from 'react-icons/fa';
import useHasMounted from '@/hooks/useHasMounted';

export function LoadingSpinner({
  loadingSubtitle: loadingSubtitleProp,
  loadingTitle: loadingTitleProp,
  fullWidth = false,
  show: showProp,
  mobileBehavior = false,
}: LoadingSpinnerProps) {
  const { t: tCommon } = useTranslation('common');

  const hasMounted = useHasMounted();

  const loadingTitle =
    loadingTitleProp || translationFallback('loading', tCommon('loading'), 'Loading');
  const loadingSubtitle =
    loadingSubtitleProp ||
    translationFallback('please_wait', tCommon('please_wait'), 'Please wait');

  if (!hasMounted) return null;

  if (fullWidth === true) {
    return (
      <Modal
        backdrop="static"
        className={clsx('tw-pointer-events-none tw-border-none tw-bg-transparent', {
          ['nine:!tw-hidden']: mobileBehavior,
        })}
        keyboard={false}
        centered
        show={showProp}
        contentClassName={'!tw-bg-transparent !tw-border-none !tw-pointer-events-none !tw-mb-28'}
        onHide={() => {}}
        backdropClassName={clsx('!tw-bg-black !tw-bg-opacity-[1] tw-blur-sm', {
          ['nine:!tw-hidden']: mobileBehavior,
        })}
      >
        <Container fluid>
          <Row className="tw-justify-center">
            <Col xs={12}>
              <Row>
                <Col xs={12} className="tw-mb-8 tw-text-center">
                  <div className="flex justify-center items-center mx-auto tw-aspect-square tw-h-auto tw-w-fit tw-rounded-full tw-border-4 !tw-border-solid tw-border-main tw-bg-transparent tw-p-5">
                    <div className="tw-aspect-square tw-rounded-full tw-bg-main tw-p-6">
                      <FaWineBottle className="tw-animate-spin tw-text-4xl tw-text-white" />
                    </div>
                    <span className="visually-hidden">
                      {translationFallback('loading', tCommon('loading'), 'Loading')}
                      ...
                    </span>
                  </div>
                </Col>
                <Col xs={12} className="tw-text-center">
                  <Row className="tw-justify-center">
                    <Col xs={12}>
                      <p className="tw-mb-1 tw-text-3xl tw-font-black tw-text-white">
                        <strong>{loadingTitle}</strong>
                      </p>
                    </Col>
                    <Col xs={12}>
                      <p className="tw-font-semibold tw-text-slate-100">
                        {loadingSubtitle}
                        ...
                      </p>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </Modal>
    );
  }

  if (showProp === false) {
    return null;
  }

  return (
    <div className={clsx('tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center')}>
      <Container fluid>
        <Row className="tw-justify-center">
          <Col xs={12}>
            <Row>
              <Col xs={12} className="tw-mb-8 tw-text-center">
                <div className="flex justify-center items-center mx-auto tw-aspect-square tw-h-auto tw-w-fit tw-rounded-full tw-border-4 !tw-border-solid tw-border-main tw-bg-transparent tw-p-5">
                  <div className="tw-aspect-square tw-rounded-full tw-bg-main tw-p-6">
                    <FaWineBottle className="tw-animate-spin tw-text-4xl tw-text-white max-md:tw-text-2xl" />
                  </div>
                  <span className="visually-hidden">
                    {translationFallback('loading', tCommon('loading'), 'Loading')}
                    ...
                  </span>
                </div>
              </Col>
              <Col xs={12} className="tw-text-center">
                <Row className="tw-justify-center">
                  <Col xs={12}>
                    <p className="tw-mb-1 tw-text-3xl tw-font-black tw-text-gray-950 max-md:tw-text-xl">
                      <strong>{loadingTitle}</strong>
                    </p>
                  </Col>
                  <Col xs={12}>
                    <p className="tw-font-semibold tw-text-gray-400 max-md:tw-text-xs">
                      {loadingSubtitle}
                      ...
                    </p>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export function LoadingOfferAuthSpinner({ show }: LoadingOfferAuthSpinnerProps) {
  const { t: tDashboard } = useTranslation('dashboard');
  const { t: tCommon } = useTranslation('common');
  const title = translationFallback(
    'uploading_images',
    tCommon('uploading_images'),
    'Uploading images'
  );
  const subtitle = translationFallback(
    'authentication.uploading_images_subtitle',
    tDashboard('authentication.uploading_images_subtitle'),
    'We are uploading your images. One moment, please. We will show you the confirmation shortly.'
  );

  return (
    <LoadingSpinner fullWidth={true} loadingSubtitle={subtitle} loadingTitle={title} show={show} />
  );
}

export function AdminSpinner({ show }: { show: boolean }) {
  return (
    <LoadingSpinner
      fullWidth={false}
      loadingSubtitle={'Please wait while we load the admin data'}
      loadingTitle={'Loading Admin Data'}
      show={show}
    />
  );
}

export function CommonSpinner({ show, fullWidth }: { show: boolean; fullWidth: boolean }) {
  const { t: tCommon } = useTranslation('common');
  const loadingTitle = translationFallback(
    'just_a_moment',
    tCommon('just_a_moment'),
    'Hang Tight!'
  );
  const loadingSubtitle = translationFallback(
    'loading_data_subtitle',
    tCommon('loading_data_subtitle'),
    "We're getting things ready for you. It won't take long!"
  );
  return (
    <LoadingSpinner
      fullWidth={fullWidth}
      loadingSubtitle={loadingSubtitle}
      loadingTitle={loadingTitle}
      show={show}
    />
  );
}

export function PasswordResetSpinner() {
  const { t: tCommon } = useTranslation('common');

  const loadingTitle = translationFallback(
    'resetting_password',
    tCommon('resetting_password'),
    'Resetting Password'
  );
  const loadingSubtitle = translationFallback(
    'resetting_password_subtitle',
    tCommon('resetting_password_subtitle'),
    'Please wait while we reset your password.'
  );

  return (
    <LoadingSpinner
      fullWidth={false}
      loadingSubtitle={loadingSubtitle}
      loadingTitle={loadingTitle}
      show={true}
    />
  );
}

export function SignUpSpinner({ localeChange = false }: { localeChange?: boolean }) {
  const { t: tCommon } = useTranslation('common');

  const loadingTitle = !localeChange
    ? translationFallback('creating_account', tCommon('creating_account'), 'Creating Account')
    : translationFallback('changing_language', tCommon('changing_language'), 'Changing Language');
  const loadingSubtitle = !localeChange
    ? translationFallback(
        'please_wait_while_we_create_your_account',
        tCommon('please_wait_while_we_create_your_account'),
        'Please wait while we create your account.'
      )
    : translationFallback(
        'please_wait_while_we_load_the_page',
        tCommon('please_wait_while_we_load_the_page'),
        'Please wait while we load the page.'
      );

  return (
    <LoadingSpinner
      fullWidth={false}
      loadingSubtitle={loadingSubtitle}
      loadingTitle={loadingTitle}
      show={true}
    />
  );
}

export function VerifiyEmailSpinner({ loadingStart }: { loadingStart: boolean }) {
  const { t: tCommon } = useTranslation('common');

  const loadingTitle =
    loadingStart === false
      ? translationFallback('verifying_email', tCommon('verifying_email'), 'Verifying Email')
      : translationFallback(
          'loading_email_verification',
          tCommon('loading_email_verification'),
          'Loading Email Verification'
        );
  const loadingSubtitle =
    loadingStart === false
      ? translationFallback(
          'please_wait_while_we_verify_your_email',
          tCommon('please_wait_while_we_verify_your_email'),
          'Please wait while we verify your email.'
        )
      : translationFallback(
          'please_wait_while_we_load_the_email_verification',
          tCommon('please_wait_while_we_load_the_email_verification'),
          'Please wait while we load the email verification.'
        );

  return (
    <LoadingSpinner
      fullWidth={false}
      loadingSubtitle={loadingSubtitle}
      loadingTitle={loadingTitle}
      show={true}
    />
  );
}
